'use client';

/**
 * Shared transport UI for the auralization engine.
 * Supports 2+ named states (Before/After, Pure/Room, Pure/Bare/Panel…).
 * Samples stay in samples.ts — single definition for the whole app.
 */

import { useRef, type MouseEvent } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { SAMPLES } from '@/components/room/audio/samples';

function fmt(s: number): string {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/** Minimal transport surface — works with useAuralization and useToolAuralization. */
export interface AuralizationTransportApi {
  ready: string[];
  loading: boolean;
  error: string | null;
  isPlaying: boolean;
  activeState: string;
  elapsed: number;
  duration: number;
  sourceName: string | null;
  loadFile: (file: File) => void;
  loadSampleUrl: (
    url: string,
    name: string,
    trim?: { startSec?: number; endSec?: number },
  ) => void;
  togglePlay: () => void;
  setActive: (s: string) => void;
  seekTo: (seconds: number) => void;
}

export interface AuralizationPlayerProps {
  api: AuralizationTransportApi;
  /** Ordered state chips. */
  states: { id: string; label: string }[];
  /** Optional status line; omit for a quieter UI (preferred on tools). */
  idleHint?: string;
  className?: string;
  showUpload?: boolean;
}

export default function AuralizationPlayer({
  api: a,
  states,
  idleHint = '',
  className = '',
  showUpload = true,
}: AuralizationPlayerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const hasSource = !!a.sourceName;
  const canPlay = a.ready.includes(a.activeState) && !a.loading;
  const pct = a.duration > 0 ? (a.elapsed / a.duration) * 100 : 0;

  const onSeek = (e: MouseEvent<HTMLDivElement>) => {
    if (!a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const f = (e.clientX - rect.left) / rect.width;
    a.seekTo(Math.max(0, Math.min(1, f)) * a.duration);
  };

  return (
    <div className={`rounded-xl border border-[var(--app-border)] bg-[var(--app-card)] p-5 ${className}`}>
      <div className="flex flex-wrap gap-2">
        {states.map((s) => {
          const active = a.activeState === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => a.setActive(s.id)}
              className={
                'inline-flex min-h-11 min-w-[4.5rem] flex-1 items-center justify-center rounded-lg px-3 text-[13px] font-semibold transition-colors ' +
                (active
                  ? 'bg-[var(--rt-accent)] text-[var(--rt-button-label)]'
                  : 'border border-[var(--app-border)] text-[var(--rt-ink-dim)] hover:border-[color-mix(in_oklch,var(--rt-accent)_50%,transparent)] hover:text-[var(--rt-ink)]')
              }
              aria-pressed={active}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={!canPlay}
          onClick={a.togglePlay}
          aria-label={a.isPlaying ? 'Pause' : 'Play'}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[var(--app-border)] text-[var(--rt-accent)] transition-colors hover:border-[var(--rt-accent)] disabled:opacity-40"
        >
          {a.loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : a.isPlaying ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="h-5 w-5" />
          )}
        </button>
        <div
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(a.duration)}
          aria-valuenow={Math.round(a.elapsed)}
          tabIndex={0}
          onClick={onSeek}
          className="h-1.5 flex-1 cursor-pointer rounded-full bg-[var(--app-border)]"
        >
          <div className="h-full rounded-full bg-[var(--rt-accent)]" style={{ width: `${pct}%` }} />
        </div>
        <span className="shrink-0 font-mono text-[11px] tabular-nums text-[var(--rt-ink-dim)]">
          {fmt(a.elapsed)} / {fmt(a.duration)}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {SAMPLES.map((s) => (
          <button
            key={s.id}
            type="button"
            title={s.hint}
            onClick={() => a.loadSampleUrl(s.url, s.label, { startSec: s.startSec, endSec: s.endSec })}
            className={
              'inline-flex min-h-11 items-center rounded-lg border px-3 text-[12px] transition-colors ' +
              (a.sourceName === s.label
                ? 'border-[var(--rt-accent)] bg-[color-mix(in_oklch,var(--rt-accent)_10%,transparent)] text-[var(--rt-accent)]'
                : 'border-[var(--app-border)] text-[var(--rt-ink-dim)] hover:border-[color-mix(in_oklch,var(--rt-accent)_50%,transparent)] hover:text-[var(--rt-ink)]')
            }
          >
            {s.label}
          </button>
        ))}
        {showUpload && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            title="Play your own audio file (use a dry/anechoic source)"
            className="rounded-lg border border-[var(--app-border)] px-2.5 py-1.5 text-[12px] text-[var(--rt-ink-dim)] transition-colors hover:border-[color-mix(in_oklch,var(--rt-accent)_50%,transparent)] hover:text-[var(--rt-ink)]"
          >
            Your file
          </button>
        )}
      </div>
      {showUpload && (
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) a.loadFile(f);
            e.currentTarget.value = '';
          }}
        />
      )}

      {(a.error || a.loading || (idleHint && !hasSource)) && (
        <p className="mt-3 min-h-[1.25rem] text-[11.5px] leading-relaxed text-[var(--rt-ink-dim)]">
          {a.error ? (
            a.error
          ) : a.loading ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Rendering the room…
            </span>
          ) : (
            idleHint
          )}
        </p>
      )}
    </div>
  );
}
